import React, { useState, useCallback } from "react";
import apiClient from "api/axiosConfig";
import PackagedTemplateForm from "./PackagedTemplateForm";
import { validateField, validateAllFormFields, parseBackendErrors } from "./utils/validation";
import { validateImageFile } from "./utils/fileHelpers";
import SuccessModal from "components/Common/SuccessModal";

const mappingInitialState = {
  language_id: "",
  email_template_id: "",
  landing_page_id: "",
  sending_profile_id: "",
  phishing_domain_id: "",
};

const initialState = {
  name: "",
  description: "",
  difficulty: "beginner",
  categories: [],
  tenant_mode: "global",
  target_tenant_id: "",
  language_mappings: [{ ...mappingInitialState }],
  image_path: null,
};

const PackagedTemplate = () => {
  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [backendError, setBackendError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidationAttempted, setIsValidationAttempted] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const handleChange = useCallback(
    (e) => {
      const { name, type, files, value } = e.target;
      let newValue = type === "file" ? files[0] : value;

      if (type === "file") {
        const { isValid, error } = validateImageFile(newValue);
        setErrors((prev) => ({ ...prev, [name]: isValid ? "" : error }));
      }

      setFormData((prev) => ({ ...prev, [name]: newValue }));

      if (isValidationAttempted && type !== "file") {
        const error = validateField(name, newValue, formData);
        setErrors((prev) => ({ ...prev, [name]: error }));
      }
    },
    [isValidationAttempted, formData]
  );

  const handleCategoryChange = useCallback(
    (name, selectedValues) => {
      setFormData((prev) => ({ ...prev, categories: selectedValues }));

      if (isValidationAttempted) {
        const error = validateField("categories", selectedValues, formData);
        setErrors((prev) => ({ ...prev, categories: error }));
      }
    },
    [isValidationAttempted, formData]
  );

  const handleSingleSelectChange = useCallback(
    (name, val) => {
      setFormData((prev) => ({ ...prev, [name]: val }));

      if (isValidationAttempted) {
        const error = validateField(name, val, formData);
        setErrors((prev) => ({ ...prev, [name]: error }));
      }
    },
    [isValidationAttempted, formData]
  );

  const handleMappingChange = useCallback(
    (index, field, value) => {
      const updatedMappings = [...formData.language_mappings];
      updatedMappings[index] = { ...updatedMappings[index], [field]: value !== "" ? Number(value) : "" };
      setFormData((prev) => ({ ...prev, language_mappings: updatedMappings }));

      if (isValidationAttempted) {
        const error = validateField("language_mappings", updatedMappings, formData);
        setErrors((prev) => ({ ...prev, language_mappings: error }));
      }
    },
    [isValidationAttempted, formData]
  );

  const addMapping = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      language_mappings: [...prev.language_mappings, { ...mappingInitialState }],
    }));
  }, []);

  const removeMapping = useCallback(
    (index) => {
      const updated = formData.language_mappings.filter((_, i) => i !== index);
      setFormData((prev) => ({ ...prev, language_mappings: updated }));
      if (isValidationAttempted) {
        const error = validateField("language_mappings", updated, formData);
        setErrors((prev) => ({ ...prev, language_mappings: error }));
      }
    },
    [formData.language_mappings, isValidationAttempted, formData]
  );

  const resetForm = useCallback(() => {
    setFormData(initialState);
    setErrors({});
    setBackendError("");
    setIsValidationAttempted(false);
    const fileInput = document.getElementById("imagePathFile");
    if (fileInput) fileInput.value = null;
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsValidationAttempted(true);
    setBackendError("");

    const { isValid, errors: validationErrors } = validateAllFormFields(formData);
    if (!isValid) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = new FormData();
      payload.append("name", formData.name.trim());
      payload.append("difficulty", formData.difficulty);
      if (formData.description) payload.append("description", formData.description);

      if (formData.image_path) {
        payload.append("image_path", formData.image_path);
      }

      if (formData.tenant_mode === "specific" && formData.target_tenant_id) {
        payload.append("target_tenant_id", parseInt(formData.target_tenant_id, 10));
      }

      formData.categories.forEach((id) => {
        payload.append("categories[]", parseInt(id, 10));
      });

      formData.language_mappings.forEach((mapping, index) => {
        payload.append(`language_mappings[${index}][language_id]`, parseInt(mapping.language_id, 10));
        payload.append(`language_mappings[${index}][email_template_id]`, parseInt(mapping.email_template_id, 10));
        payload.append(`language_mappings[${index}][landing_page_id]`, parseInt(mapping.landing_page_id, 10));
        payload.append(`language_mappings[${index}][sending_profile_id]`, parseInt(mapping.sending_profile_id, 10));
        payload.append(`language_mappings[${index}][phishing_domain_id]`, parseInt(mapping.phishing_domain_id, 10));
      });

      const response = await apiClient.post("/packaged-templates", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSuccessMessage(response?.data?.message || "Packaged Template created successfully!");
      setShowSuccessModal(true);
      resetForm();
    } catch (error) {
      console.error(error);
      const { fieldErrors, backendErrorText } = parseBackendErrors(error);
      if (Object.keys(fieldErrors).length > 0) {
        setErrors((prev) => ({ ...prev, ...fieldErrors }));
      }
      setBackendError(backendErrorText);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <PackagedTemplateForm
        formData={formData}
        errors={errors}
        backendError={backendError}
        isSubmitting={isSubmitting}
        handleChange={handleChange}
        handleCategoryChange={handleCategoryChange}
        handleSingleSelectChange={handleSingleSelectChange}
        handleMappingChange={handleMappingChange}
        addMapping={addMapping}
        removeMapping={removeMapping}
        handleSubmit={handleSubmit}
      />

      <SuccessModal
        show={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        message={successMessage}
      />
    </>
  );
};

export default PackagedTemplate;